import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  ExternalLink,
  Globe2,
  KeyRound,
  MailCheck,
  Route,
  ShieldCheck,
  X,
} from 'lucide-react';

interface GoogleEmailAuthenticationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'dkim' | 'spf' | 'dmarc' | 'validation' | 'bot';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'vue', label: 'Vue d’ensemble', icon: Route },
  { id: 'dkim', label: 'DKIM', icon: KeyRound },
  { id: 'spf', label: 'SPF', icon: MailCheck },
  { id: 'dmarc', label: 'DMARC', icon: ShieldCheck },
  { id: 'validation', label: 'Validation', icon: ClipboardCheck },
  { id: 'bot', label: 'Contrat bot', icon: Bot },
];

const exampleSubdomain = 'pma';
const exampleDomain = `${exampleSubdomain}.bzinc.ai`;

function Panel({ title, icon: Icon, children, tone = 'blue' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'blue' | 'amber' | 'emerald' | 'rose';
}) {
  const tones = {
    blue: 'border-blue-300/20 bg-blue-400/5',
    amber: 'border-amber-300/25 bg-amber-400/10',
    emerald: 'border-emerald-300/20 bg-emerald-400/5',
    rose: 'border-rose-300/25 bg-rose-400/10',
  };

  return (
    <section className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-blue-300" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function CopyValue({ children, label = 'Copier la valeur' }: { children: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <div className="group relative my-3 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
      <pre className="overflow-x-auto whitespace-pre-wrap break-all p-4 pr-12 text-xs leading-6 text-emerald-300"><code>{children}</code></pre>
      <button type="button" onClick={() => void copy()} className="absolute right-2 top-2 rounded-lg border border-slate-600 bg-slate-800 p-2 text-slate-300 transition hover:bg-slate-700" title={label} aria-label={label}>
        {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
      <span className="sr-only" aria-live="polite">{copied ? 'Valeur copiée.' : copyFailed ? 'La copie a échoué.' : ''}</span>
    </div>
  );
}

function Field({ label, value, accent = 'blue' }: { label: string; value: string; accent?: 'blue' | 'green' | 'red' }) {
  const accentClass = { blue: 'text-blue-300', green: 'text-emerald-300', red: 'text-rose-300' }[accent];
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <p className={`text-[11px] font-black uppercase tracking-[0.18em] ${accentClass}`}>{label}</p>
      <p className="mt-2 break-all font-mono text-sm font-semibold text-white">{value}</p>
    </div>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-300">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function GoogleAdminVisual() {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-100 text-slate-900 shadow-2xl" aria-label="Repère visuel de la console Google Admin">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <span className="h-3 w-3 rounded-full bg-[#4285f4]" />
        <span className="h-3 w-3 rounded-full bg-[#ea4335]" />
        <span className="h-3 w-3 rounded-full bg-[#fbbc05]" />
        <span className="h-3 w-3 rounded-full bg-[#34a853]" />
        <span className="ml-2 text-sm font-bold">Google Admin</span>
      </div>
      <div className="grid md:grid-cols-[190px_1fr]">
        <div className="border-r border-slate-200 bg-white p-4 text-xs leading-8">
          <p className="font-bold text-slate-500">Apps</p>
          <p className="pl-3 font-bold text-slate-600">Google Workspace</p>
          <p className="rounded-lg bg-blue-100 px-3 font-black text-blue-700">Gmail</p>
        </div>
        <div className="p-4">
          <p className="mb-3 text-xs font-semibold text-slate-500">Apps › Google Workspace › Settings for Gmail</p>
          <div className="rounded-xl border-2 border-blue-500 bg-white p-4 shadow-sm">
            <p className="font-black">Authenticate email</p>
            <p className="mt-1 text-xs text-slate-500">Set up email authentication (DKIM)</p>
          </div>
          <p className="mt-3 text-xs font-bold text-blue-700">Repère : ouvre cette section pour générer la clé DKIM.</p>
        </div>
      </div>
    </div>
  );
}

function DnsVisual({ title, name, content, accent }: { title: string; name: string; content: string; accent: 'blue' | 'green' | 'red' }) {
  const colors = {
    blue: 'border-blue-400/50 bg-blue-400/10 text-blue-200',
    green: 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200',
    red: 'border-rose-400/50 bg-rose-400/10 text-rose-200',
  };
  return (
    <div className={`overflow-hidden rounded-2xl border ${colors[accent]}`}>
      <div className="border-b border-white/10 px-4 py-3 font-black">{title}</div>
      <div className="grid gap-3 p-4 md:grid-cols-[1fr_90px_2fr_90px]">
        <Field label="Name / Host" value={name} accent={accent} />
        <Field label="Type" value="TXT" accent={accent} />
        <Field label="Content / Value" value={content} accent={accent} />
        <Field label="TTL" value="Auto" accent={accent} />
      </div>
    </div>
  );
}

function VueTab() {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        {[
          ['1', 'DKIM', 'Google signe chaque message avec une clé cryptographique.', 'bg-blue-500'],
          ['2', 'SPF', 'Le DNS autorise les serveurs Google à envoyer pour le sous-domaine.', 'bg-emerald-500'],
          ['3', 'DMARC', 'Les messages non alignés sont rejetés selon la politique stricte.', 'bg-rose-500'],
        ].map(([number, title, text, color]) => (
          <div key={title} className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className={`absolute inset-y-0 left-0 w-1 ${color}`} />
            <div className="flex items-center gap-3"><span className={`flex h-9 w-9 items-center justify-center rounded-full ${color} font-black text-white`}>{number}</span><h3 className="text-xl font-black text-white">{title}</h3></div>
            <p className="mt-3 text-sm leading-6 text-slate-300">{text}</p>
          </div>
        ))}
      </div>
      <Panel title="Ordre obligatoire" icon={Route} tone="amber">
        <ol className="grid gap-3 md:grid-cols-4">
          {['Créer DKIM', 'Publier SPF', 'Valider les deux', 'Publier DMARC'].map((step, index) => (
            <li key={step} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3 text-sm font-bold text-white">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950">{index + 1}</span>{step}
            </li>
          ))}
        </ol>
        <div className="mt-4 rounded-xl border border-rose-300/25 bg-rose-400/10 p-4 text-sm font-semibold leading-6 text-rose-100">
          La politique DMARC demandée est <code>p=reject</code>. Elle peut bloquer des envois légitimes mal configurés : ne la publier qu’après avoir confirmé DKIM et SPF.
        </div>
      </Panel>
      <Panel title="Exemple utilisé dans la procédure" icon={Globe2}>
        <div className="grid gap-3 md:grid-cols-2"><Field label="Sous-domaine" value={exampleSubdomain} /><Field label="Domaine complet" value={exampleDomain} /></div>
        <p className="mt-3 text-xs leading-5 text-slate-400">Remplace <strong className="text-white">pma</strong> par le sous-domaine du client. Si le portail DNS ajoute automatiquement <code>.bzinc.ai</code>, saisis seulement la partie affichée dans la colonne « Nom court ».</p>
      </Panel>
    </div>
  );
}

function DkimTab() {
  return (
    <div className="space-y-5">
      <Panel title="1 — Ouvrir l’authentification DKIM" icon={KeyRound}>
        <p className="mb-4 text-sm leading-6 text-slate-300">Connecte-toi avec un compte super-administrateur, puis suis exactement :</p>
        <CopyValue>Apps → Google Workspace → Gmail → Authenticate email</CopyValue>
        <GoogleAdminVisual />
      </Panel>
      <Panel title="2 — Générer l’enregistrement" icon={KeyRound}>
        <ol className="space-y-3 text-sm leading-6 text-slate-300">
          <li><strong className="text-white">1.</strong> Dans « Selected domain », choisis le domaine ou sous-domaine à configurer.</li>
          <li><strong className="text-white">2.</strong> Clique <strong className="text-white">Generate new record</strong>.</li>
          <li><strong className="text-white">3.</strong> Choisis une clé de <strong className="text-white">2048 bits</strong>.</li>
          <li><strong className="text-white">4.</strong> Garde le préfixe du sélecteur <strong className="text-white">google</strong>, sauf convention différente déjà documentée.</li>
          <li><strong className="text-white">5.</strong> Copie exactement le nom d’hôte et la longue valeur TXT générés par Google.</li>
        </ol>
      </Panel>
      <Panel title="3 — Créer le TXT dans le DNS" icon={Globe2}>
        <DnsVisual title="Exemple DKIM — la valeur réelle vient de Google" name="google._domainkey.pma" content="v=DKIM1; k=rsa; p=CLÉ_PUBLIQUE_GÉNÉRÉE_PAR_GOOGLE" accent="blue" />
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field label="Nom court dans la zone bzinc.ai" value="google._domainkey.pma" />
          <Field label="Nom complet (FQDN)" value="google._domainkey.pma.bzinc.ai" />
        </div>
        <Bullets items={[
          <>Le contenu commence par <code className="text-blue-200">v=DKIM1;</code> et doit être copié au complet, sur une seule valeur TXT.</>,
          <>Ne pas ajouter les guillemets : certains portails les affichent automatiquement.</>,
          <>Ne jamais utiliser la clé montrée dans un exemple ou une capture; utiliser uniquement celle générée dans le compte Google du client.</>,
        ]} />
      </Panel>
      <Panel title="4 — Activer la signature" icon={CheckCircle2} tone="emerald">
        <p className="text-sm leading-6 text-slate-300">Après la propagation DNS, retourne dans Google Admin, sélectionne le domaine et clique <strong className="text-white">Start authentication</strong>. Le statut doit confirmer que l’authentification DKIM est active.</p>
      </Panel>
    </div>
  );
}

function SpfTab() {
  const spf = 'v=spf1 include:_spf.google.com ~all';
  return (
    <div className="space-y-5">
      <Panel title="Créer l’enregistrement SPF" icon={MailCheck}>
        <DnsVisual title="SPF Google Workspace" name={exampleSubdomain} content={spf} accent="green" />
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field label="Nom court dans la zone bzinc.ai" value={exampleSubdomain} accent="green" />
          <Field label="Nom complet (FQDN)" value={exampleDomain} accent="green" />
        </div>
        <p className="mt-4 text-sm text-slate-300">Valeur officielle Google Workspace lorsque Google est le seul expéditeur :</p>
        <CopyValue>{spf}</CopyValue>
      </Panel>
      <Panel title="Garde-fous SPF" icon={AlertTriangle} tone="amber">
        <Bullets items={[
          <><strong className="text-white">Un seul enregistrement SPF</strong> doit exister pour {exampleDomain}. S’il existe déjà, il faut le fusionner; ne jamais créer un deuxième TXT commençant par <code>v=spf1</code>.</>,
          <>Le mécanisme officiel Google est <code className="text-emerald-200">include:_spf.google.com</code>. L’ajout de fournisseurs doit être justifié par les sources d’envoi réelles.</>,
          <>Ne pas ajouter les guillemets autour de la valeur dans le champ DNS, sauf exigence explicite du fournisseur.</>,
          <>Le <code>~all</code> produit un « soft fail ». Ne le remplacer par <code>-all</code> qu’après inventaire et validation de tous les expéditeurs.</>,
        ]} />
      </Panel>
    </div>
  );
}

function DmarcTab() {
  const dmarc = 'v=DMARC1; p=reject; adkim=s; aspf=s;';
  return (
    <div className="space-y-5">
      <Panel title="Créer l’enregistrement DMARC" icon={ShieldCheck}>
        <DnsVisual title="DMARC strict" name="_dmarc.pma" content={dmarc} accent="red" />
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field label="Nom court dans la zone bzinc.ai" value="_dmarc.pma" accent="red" />
          <Field label="Nom complet (FQDN)" value="_dmarc.pma.bzinc.ai" accent="red" />
        </div>
        <CopyValue>{dmarc}</CopyValue>
      </Panel>
      <Panel title="Ce que la politique impose" icon={ShieldCheck} tone="rose">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="p=reject" value="Rejeter les messages qui échouent DMARC" accent="red" />
          <Field label="adkim=s" value="Alignement DKIM strict" accent="red" />
          <Field label="aspf=s" value="Alignement SPF strict" accent="red" />
        </div>
        <p className="mt-4 text-sm font-semibold leading-6 text-rose-100">Publier seulement après la réussite des validations DKIM et SPF. Google recommande de configurer SPF et DKIM au moins 48 heures avant une politique DMARC restrictive.</p>
      </Panel>
    </div>
  );
}

function ValidationTab() {
  return (
    <div className="space-y-5">
      <Panel title="Valider dans MXToolbox — aucun terminal requis" icon={Globe2} tone="blue">
        <p className="text-sm leading-6 text-slate-300">
          Remplace <code>pma.bzinc.ai</code> par le domaine à tester. Ouvre chaque outil ci-dessous, inscris la valeur indiquée, puis confirme le résultat attendu. Chaque bouton ouvre l’outil choisi dans un nouvel onglet.
        </p>
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <article className="rounded-xl border border-emerald-300/20 bg-emerald-400/5 p-4">
            <p className="font-black text-emerald-300">1. SPF Record Lookup</p>
            <p className="mt-2 text-sm leading-6 text-slate-300">Saisis <code>pma.bzinc.ai</code>. Confirme qu’un seul SPF est trouvé et que <code>include:_spf.google.com</code> autorise Google.</p>
            <a className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-400 px-4 py-2 text-sm font-black text-slate-950 transition hover:bg-emerald-300" href="https://mxtoolbox.com/spf.aspx" target="_blank" rel="noreferrer">Ouvrir le test SPF <ExternalLink className="h-4 w-4" /></a>
          </article>
          <article className="rounded-xl border border-blue-300/20 bg-blue-400/5 p-4">
            <p className="font-black text-blue-300">2. DKIM Lookup</p>
            <p className="mt-2 text-sm leading-6 text-slate-300">Saisis <code>pma.bzinc.ai:google</code> — domaine, deux-points, puis sélecteur. Remplace <code>google</code> si Google Admin en a généré un autre. Confirme que la clé publique est trouvée.</p>
            <a className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-400 px-4 py-2 text-sm font-black text-slate-950 transition hover:bg-blue-300" href="https://mxtoolbox.com/dkim.aspx" target="_blank" rel="noreferrer">Ouvrir le test DKIM <ExternalLink className="h-4 w-4" /></a>
          </article>
          <article className="rounded-xl border border-rose-300/20 bg-rose-400/5 p-4">
            <p className="font-black text-rose-300">3. DMARC Lookup</p>
            <p className="mt-2 text-sm leading-6 text-slate-300">Saisis <code>pma.bzinc.ai</code>. Confirme que le résultat affiche <code>p=reject</code>, <code>adkim=s</code> et <code>aspf=s</code>.</p>
            <a className="mt-4 inline-flex items-center gap-2 rounded-lg bg-rose-400 px-4 py-2 text-sm font-black text-slate-950 transition hover:bg-rose-300" href="https://mxtoolbox.com/dmarc.aspx" target="_blank" rel="noreferrer">Ouvrir le test DMARC <ExternalLink className="h-4 w-4" /></a>
          </article>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Field label="DKIM attendu" value="v=DKIM1; ..." />
          <Field label="SPF attendu" value="v=spf1 include:_spf.google.com ~all" accent="green" />
          <Field label="DMARC attendu" value="v=DMARC1; p=reject; adkim=s; aspf=s;" accent="red" />
        </div>
        <div className="mt-4 rounded-xl border border-amber-300/25 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
          Un résultat vert confirme surtout la publication DNS. Termine avec un vrai envoi et « Afficher l’original » dans Gmail pour obtenir <strong>SPF: PASS, DKIM: PASS et DMARC: PASS</strong>. Après une modification DNS, attends la propagation et relance les trois tests si l’ancienne valeur apparaît encore.
        </div>
      </Panel>
      <Panel title="Test de bout en bout" icon={MailCheck}>
        <ol className="space-y-3 text-sm leading-6 text-slate-300">
          {[
            'Dans Google Admin, confirmer que DKIM affiche un statut actif.',
            'Envoyer un courriel depuis le nouveau domaine vers une boîte externe de test.',
            'Dans Gmail, ouvrir le message → Plus → Afficher l’original.',
            'Confirmer SPF: PASS, DKIM: PASS et DMARC: PASS.',
            'Confirmer que le domaine DKIM et le MAIL FROM sont alignés strictement avec le domaine visible dans From.',
          ].map((step, index) => <li key={step} className="flex gap-3 rounded-xl border border-white/10 bg-black/20 p-4"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-400 font-black text-slate-950">{index + 1}</span><span>{step}</span></li>)}
        </ol>
      </Panel>
      <Panel title="Références officielles Google" icon={ExternalLink}>
        <div className="flex flex-col gap-3 text-sm">
          <a className="text-blue-300 underline hover:text-blue-200" href="https://support.google.com/a/answer/174124" target="_blank" rel="noreferrer">Configurer DKIM — Google Workspace Admin Help</a>
          <a className="text-blue-300 underline hover:text-blue-200" href="https://support.google.com/a/answer/33786" target="_blank" rel="noreferrer">Configurer SPF — Google Workspace Admin Help</a>
          <a className="text-blue-300 underline hover:text-blue-200" href="https://support.google.com/a/answer/2466580" target="_blank" rel="noreferrer">Configurer DMARC — Google Workspace Admin Help</a>
        </div>
      </Panel>
    </div>
  );
}

function BotTab() {
  const rules = [
    'Confirmer le sous-domaine exact, la zone DNS visée et le compte Google Workspace avant toute modification.',
    'Lire les enregistrements TXT existants avant d’écrire; ne jamais remplacer aveuglément un SPF ou un DMARC existant.',
    'Générer DKIM dans Google Admin avec une clé de 2048 bits et utiliser exactement la valeur retournée par Google.',
    'Ne jamais inventer, raccourcir, reformater ou enregistrer dans Git la valeur DKIM.',
    'Pour SPF, fusionner les expéditeurs autorisés dans un seul enregistrement et conserver include:_spf.google.com pour Google Workspace.',
    'Ne publier p=reject qu’après confirmation explicite que SPF et DKIM passent pour toutes les sources d’envoi autorisées.',
    'Après chaque écriture, valider le DNS public dans MXToolbox avec les tests SPF, DKIM et DMARC, puis comparer les valeurs complètes.',
    'Effectuer un envoi réel et confirmer SPF, DKIM et DMARC à PASS avant de déclarer la tâche terminée.',
    'Rapporter le nom, le type, le TTL, les validations et les résultats sans exposer de secret ni de donnée de compte.',
  ];
  return (
    <div className="space-y-5">
      <Panel title="Contrat d’exécution pour un bot" icon={Bot} tone="amber">
        <ol className="space-y-3 text-sm leading-6 text-slate-200">
          {rules.map((rule, index) => <li key={rule} className="flex gap-3 rounded-xl border border-white/10 bg-black/20 p-4"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/20 font-black text-amber-200">{index + 1}</span><span>{rule}</span></li>)}
        </ol>
      </Panel>
      <Panel title="Conditions d’arrêt obligatoire" icon={AlertTriangle} tone="rose">
        <Bullets items={[
          <>Le domaine sélectionné dans Google ne correspond pas au sous-domaine demandé.</>,
          <>Plus d’un SPF existe ou une source d’envoi active n’est pas identifiée.</>,
          <>Un DKIM, SPF ou DMARC existant serait écrasé sans validation de son propriétaire.</>,
          <>SPF ou DKIM ne passe pas : ne pas publier <code>p=reject</code>.</>,
          <>Le bot ne possède pas l’autorisation ou l’accès requis au DNS ou à Google Admin.</>,
        ]} />
      </Panel>
    </div>
  );
}

export default function GoogleEmailAuthenticationModal({ isOpen, onClose }: GoogleEmailAuthenticationModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    setActiveTab('vue');
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'));
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen, onClose]);

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, currentId: TabId) => {
    const currentIndex = tabs.findIndex((tab) => tab.id === currentId);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    const nextId = tabs[nextIndex].id;
    setActiveTab(nextId);
    window.requestAnimationFrame(() => document.getElementById(`google-email-tab-${nextId}`)?.focus());
  };

  const content = {
    vue: <VueTab />, dkim: <DkimTab />, spf: <SpfTab />, dmarc: <DmarcTab />, validation: <ValidationTab />, bot: <BotTab />,
  }[activeTab];

  return (
    <AnimatePresence>{isOpen && (
      <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-3 backdrop-blur-sm md:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose} role="dialog" aria-modal="true" aria-labelledby="google-email-modal-title">
        <motion.div ref={dialogRef} tabIndex={-1} className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-blue-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 shadow-2xl outline-none" initial={{ y: 30, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.98 }} onMouseDown={(event) => event.stopPropagation()}>
          <header className="relative flex shrink-0 items-start justify-between gap-4 overflow-hidden border-b border-white/10 bg-black/20 px-5 py-5 md:px-7">
            <div className="absolute inset-x-0 top-0 flex h-1"><span className="flex-1 bg-[#4285f4]" /><span className="flex-1 bg-[#ea4335]" /><span className="flex-1 bg-[#fbbc05]" /><span className="flex-1 bg-[#34a853]" /></div>
            <div className="flex gap-4"><div className="rounded-2xl bg-blue-400/10 p-3"><MailCheck className="h-7 w-7 text-blue-300" /></div><div><p className="text-xs font-black uppercase tracking-[0.25em] text-blue-300">Procédure interne BZ</p><h2 id="google-email-modal-title" className="mt-1 text-2xl font-black text-white md:text-3xl">Configuration courriel Google</h2><p className="mt-1 text-sm text-slate-300">DKIM · SPF · DMARC pour un sous-domaine bzinc.ai</p></div></div>
            <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Fermer"><X className="h-5 w-5" /></button>
          </header>
          <nav role="tablist" className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:px-7" aria-label="Sections de la procédure Google">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                id={`google-email-tab-${id}`}
                role="tab"
                aria-selected={activeTab === id}
                aria-controls={`google-email-panel-${id}`}
                tabIndex={activeTab === id ? 0 : -1}
                type="button"
                onClick={() => setActiveTab(id)}
                onKeyDown={(event) => handleTabKeyDown(event, id)}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-blue-400 text-slate-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
              >
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </nav>
          <div id={`google-email-panel-${activeTab}`} role="tabpanel" aria-labelledby={`google-email-tab-${activeTab}`} className="overflow-y-auto p-4 md:p-7">{content}</div>
        </motion.div>
      </motion.div>
    )}</AnimatePresence>
  );
}
